import { DomainEntities, ProfileFormValues } from '@/pages/UserAccount/Profile';

import { isRegionKyiv, isRegionSevastopol } from '@/features/ui/ProfileChapters';

import { IntegrationSystem } from '@/shared/types';

import { AuthContextInterface } from '@/contexts/Auth/AuthContext';

import { AddressFieldName, DocumentTypeEntity, AddressType, DocumentTypeCode } from '@/api/types';

const defaultValues: ProfileFormValues = {
  // Місце народження
  countryOfBirth: '',
  regionOfBirth: '',
  districtOfBirth: '',
  cityOfBirth: '',

  // Зареєстроване місце проживання
  countryOfReg: null,
  regionOfReg: null,
  districtOfReg: null,
  cityOfReg: null,
  streetOfReg: null,
  buildingOfReg: '',
  houseOfReg: '',
  apartmentOfReg: '',

  // Фактичне місце проживання
  actualCountry: null,
  actualRegion: null,
  actualDistrict: null,
  actualCity: null,
  actualStreet: null,
  actualHouse: '',
  actualApartment: '',
  actualBuilding: '',

  [AddressType.LIVING]: null,
  actualDocTypeCode: undefined,
  actualAddressEqualsResidence: false,

  // Дані документа, що підтверджує зареєстроване місце проживання
  [AddressType.REGISTRATION]: null,
  registeredDocTypeCode: undefined,

  documents: {
    [AddressType.REGISTRATION]: {
      info: {},
      type: null,
    },
    [AddressType.LIVING]: {
      info: {},
      type: null,
    },
  },
};

export const buildFormDefaultValues = (
  authContext: AuthContextInterface,
  domainEntities: Partial<DomainEntities>,
): ProfileFormValues => {
  const registeredDocType =
    (domainEntities.regAddressDocumentTypeEntities as Array<DocumentTypeEntity<IntegrationSystem.ERZ>>)?.find(
      ({ code }) =>
        code === authContext.documentsData[AddressType.REGISTRATION]?.type ||
        code === DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI,
    ) ?? null;

  const actualDocType =
    (domainEntities.actualAddressDocumentTypeEntities as Array<DocumentTypeEntity<IntegrationSystem.ERZ>>)?.find(
      ({ code }) => code === authContext.documentsData[AddressType.LIVING]?.type,
    ) ?? null;

  const specialRegionLiving =
    isRegionKyiv(
      (typeof authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.settlement] !== 'string' &&
        authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.settlement]?.id) ||
        '',
    ) ||
    isRegionSevastopol(
      (typeof authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.settlement] !== 'string' &&
        authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.settlement]?.id) ||
        '',
    );
  const specialRegion =
    isRegionKyiv(
      (typeof authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.settlement] !== 'string' &&
        authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.settlement]?.id) ||
        '',
    ) ||
    isRegionSevastopol(
      (typeof authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.settlement] !== 'string' &&
        authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.settlement]?.id) ||
        '',
    );

  const countryOfReg =
    domainEntities.optionsCountry?.find(
      ({ id }) => id === authContext.addressesData[AddressType.REGISTRATION]?.country?.id,
    ) ?? null;
  const districtOfReg =
    domainEntities.optionsDistrictReg?.find(
      ({ id }) => id === authContext.addressesData[AddressType.REGISTRATION]?.district?.id,
    ) ?? null;
  const regionOfReg =
    domainEntities.optionsRegion?.find(
      ({ id }) => id === authContext.addressesData[AddressType.REGISTRATION]?.region?.id,
    ) ?? null;

  const streetField = authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.street];

  const streetOfReg =
    typeof streetField === 'object' && streetField !== null
      ? (domainEntities.optionsStreetReg?.find((option) => option.id === streetField.id) ?? streetField)
      : typeof streetField === 'string'
        ? streetField
        : null;

  const settlementField = authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.settlement];

  const cityOfReg = specialRegion
    ? regionOfReg
    : typeof settlementField === 'object' && settlementField !== null
      ? (domainEntities.optionsCityReg?.find((option) => option.id === settlementField.id) ?? settlementField)
      : typeof settlementField === 'string'
        ? settlementField
        : null;
  const houseOfReg = authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.house] ?? '';
  const buildingOfReg = authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.building] ?? '';
  const apartmentOfReg = authContext.addressesData[AddressType.REGISTRATION]?.[AddressFieldName.apartment] ?? '';

  const actualCountry =
    domainEntities.optionsCountry?.find(
      ({ id }) => id === authContext.addressesData[AddressType.LIVING]?.country?.id,
    ) ?? null;
  const actualDistrict =
    domainEntities.optionsDistrictLiving?.find(
      ({ id }) => id === authContext.addressesData[AddressType.LIVING]?.district?.id,
    ) ?? null;
  const actualRegion =
    domainEntities.optionsRegion?.find(({ id }) => id === authContext.addressesData[AddressType.LIVING]?.region?.id) ??
    null;
  const livingStreetField = authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.street];

  const actualStreet =
    typeof livingStreetField === 'object' && livingStreetField !== null
      ? (domainEntities.optionsStreetLiving?.find((option) => option.id === livingStreetField.id) ?? livingStreetField)
      : typeof livingStreetField === 'string'
        ? livingStreetField
        : null;

  const livingSettlementField = authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.settlement];

  const actualCity = specialRegionLiving
    ? actualRegion
    : typeof livingSettlementField === 'object' && livingSettlementField !== null
      ? (domainEntities.optionsCityLiving?.find((option) => option.id === livingSettlementField.id) ??
        livingSettlementField)
      : typeof livingSettlementField === 'string'
        ? livingSettlementField
        : null;
  const actualHouse = authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.house] ?? '';
  const actualBuilding = authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.building] ?? '';
  const actualApartment = authContext.addressesData[AddressType.LIVING]?.[AddressFieldName.apartment] ?? '';

  function toDocumentTypeEntity(
    code: DocumentTypeCode | 'null' | null | undefined,
  ): DocumentTypeEntity<IntegrationSystem.ERZ>['code'] | null {
    if (!code || code === 'null') return null;
    // if DocumentTypeEntity – its object with field code, make this way:
    return code as DocumentTypeEntity<IntegrationSystem.ERZ>['code'];
  }

  const documents = Object.entries(authContext.documentsData).reduce<ProfileFormValues['documents']>(
    (acc, [key, value]) => {
      if (key === AddressType.REGISTRATION) {
        return {
          ...acc,
          [key]: {
            info: { ...value?.fields },
            type: toDocumentTypeEntity(registeredDocType?.code),
          },
        };
      }
      if (key === AddressType.LIVING) {
        return {
          ...acc,
          [key]: {
            info: { ...value?.fields },
            type: toDocumentTypeEntity(actualDocType?.code),
          },
        };
      }

      return {
        ...acc,
      };
    },
    defaultValues.documents,
  );
  return {
    actualAddressEqualsResidence: authContext.address_equal,
    REGISTRATION: registeredDocType,
    registeredDocTypeCode: registeredDocType?.code,
    LIVING: actualDocType,
    actualDocTypeCode: actualDocType?.code,

    countryOfBirth: authContext.addressesData[AddressType.BIRTH]?.country ?? '',
    districtOfBirth: authContext.addressesData[AddressType.BIRTH]?.district ?? '',
    regionOfBirth: authContext.addressesData[AddressType.BIRTH]?.region ?? '',
    cityOfBirth: authContext.addressesData[AddressType.BIRTH]?.settlement ?? '',

    countryOfReg,
    regionOfReg,
    districtOfReg,
    cityOfReg,
    streetOfReg,
    houseOfReg,
    buildingOfReg,
    apartmentOfReg,

    actualCountry: authContext.address_equal ? countryOfReg : actualCountry,
    actualDistrict: authContext.address_equal ? districtOfReg : actualDistrict,
    actualRegion: authContext.address_equal ? regionOfReg : actualRegion,
    actualStreet: authContext.address_equal ? streetOfReg : actualStreet,
    actualCity: authContext.address_equal ? cityOfReg : actualCity,
    actualHouse: authContext.address_equal ? houseOfReg : actualHouse,
    actualBuilding: authContext.address_equal ? buildingOfReg : actualBuilding,
    actualApartment: authContext.address_equal ? apartmentOfReg : actualApartment,
    documents,
  };
};
